import {Fragment, useState} from 'react';
import React from "react";
import {cancelOrder, createInvoice, getOrdersReport, markOrderPickedUp} from "../../api/eventGroupApiClient";
import ListEdit from "../UI/ListEdit";
import Box from "../UI/Box";
import {toast} from "react-toastify";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCircleCheck, faTriangleExclamation} from "@fortawesome/free-solid-svg-icons";
import {
    convertToDisplayDate,
    convertToDisplayDateTime,
    getOrderStatusBadge,
    getPaymentMethodLabel,
    getPaymentStatusBadge,
    getPaymentStatuses,
    needsPaymentFollowUp,
    ORDER_SOURCE_WEBSHOP,
    ORDER_STATUS_CONFIRMED
} from "../../utility/utility";
import {useCustomers} from "../../hooks/useCustomers";
import {useOrders} from "../../hooks/useOrders";
import {useOrdersTotal} from "../../hooks/useOrdersTotal";
import Modal from "../UI/Modal";
import ModalForm from "../UI/ModalForm";
import Button from "../UI/Button";
import {useNavigate} from "react-router-dom";

const OrderList = (props) => {
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [submitCount, setSubmitCount] = useState(0);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [cancelOrderId, setCancelOrderId] = useState(null);
    const [filterCustomerId, setFilterCustomerId] = useState(null);
    const [filterPaymentStatus, setFilterPaymentStatus] = useState(null);
    const [filterFromDate, setFilterFromDate] = useState(null);
    const [filterToDate, setFilterToDate] = useState(null);
    const {orders, pagination} = useOrders(filterCustomerId, filterPaymentStatus, filterFromDate, filterToDate, page, pageSize, submitCount);
    const {total} = useOrdersTotal(filterFromDate, filterToDate);
    const {customers} = useCustomers(1, 200, null, null, 'active', null, 0);
    const navigate = useNavigate();

    const handleMarkPickedUpClick = (orderId) => {
        markOrderPickedUp(orderId)
            .then(() => {
                setSubmitCount((prevCount) => prevCount + 1);
                toast.success('Success!');
            })
            .catch((error) => {
                toast.error(error.message);
            });
    };

    const orderList = orders.map((item) => {
        return {
            orderId       : item.orderId,
            orderNumber   : item.number,
            status        : getOrderStatusBadge(item.status),
            paymentStatus : <div className="flex flex-col items-start gap-1">
                {getPaymentStatusBadge(item.paymentStatus)}
                {needsPaymentFollowUp(item) && (
                    <span
                        title="The payment failed but the order stays confirmed - this one needs manual follow-up."
                        className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700"
                    >
                        <FontAwesomeIcon icon={faTriangleExclamation} className="text-[10px]"/>
                        Needs follow-up
                    </span>
                )}
            </div>,
            customer      : item.customerName,
            orderDate     : convertToDisplayDate(item.createdAt),
            paymentMethod : getPaymentMethodLabel(item.paymentMethod),
            invoiceNumber : item.invoiceSerialNumber,
            productName   : item.productName,
            origin        : item.origin,
            bruttoPrice   : item.bruttoPrice + ' ' + item.currency,
            pickedUp      : item.source !== ORDER_SOURCE_WEBSHOP
                ? <span className="text-gray-300">—</span>
                : item.pickedUpAt
                    ? <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                        <FontAwesomeIcon icon={faCircleCheck} className="text-[11px]"/>
                        {convertToDisplayDateTime(item.pickedUpAt)}
                    </span>
                    : item.status === ORDER_STATUS_CONFIRMED
                        ? <button
                            type="button"
                            onClick={() => handleMarkPickedUpClick(item.orderId)}
                            className="rounded-full border border-pink-300 bg-pink-50 px-2.5 py-1 text-xs font-semibold text-pink-600 transition-colors hover:cursor-pointer hover:bg-pink-100"
                        >
                            Mark picked up
                        </button>
                        : <span className="text-gray-300">—</span>
        };
    });

    const handlePageChange = (pageNumber) => {
        setPage(pageNumber);
    };

    const handlePageSizeChange = (pageSize) => {
        setPageSize(pageSize);
        setPage(1);

    };

    const handleInvoiceIconClick = (orderId) => {
        // eslint-disable-next-line no-restricted-globals
        if (confirm('Are you sure?')) {
            createInvoice(orderId)
                .then(() => {
                    setSubmitCount(prevCount => prevCount + 1);
                    toast.success('Success!');
                })
                .catch(
                    (error) => {
                        toast.error(error.message);
                    }
                );
        }
    }

    const handleCancelIconClick = (orderId) => {
        // eslint-disable-next-line no-restricted-globals
        setShowCancelModal(true);
        setCancelOrderId(orderId);
    }

    const handleCancelSubmit = (event) => {
        event.preventDefault();

        const orderId = cancelOrderId;

        cancelOrder(orderId)
            .then(() => {
                setShowCancelModal(false);
                setSubmitCount(prevCount => prevCount + 1);
                toast.success('Success!');
            })
            .catch(
                (error) => {
                    toast.error(error.message);
                }
            );
    }

    const handleDownloadReportButtonClick = async () => {
        if (!filterFromDate || !filterToDate) {
            alert('Please select the date range.');
            return;
        }

        // DD-MM-YYYY formátum
        const from = filterFromDate.split('-').reverse().join('-');
        const to = filterToDate.split('-').reverse().join('-');

        try {
            const blob = await getOrdersReport(filterFromDate, filterToDate);

            // Create a URL for the blob
            const url = window.URL.createObjectURL(blob);

            // Create a temporary <a> element to trigger the download
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `sales_${from}_${to}.csv`);
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        }
        catch (error) {
            console.error('Failed to download the report:', error.message);
            alert('Failed to download the report. Please try again.');
        }
    };

    const listHeaders = [
        'Id',
        'Order number',
        'Status',
        'Payment status',
        'Customer name',
        'Order date',
        'Payment method',
        'Invoice',
        'Product',
        'Origin',
        'Brutto price',
        'Picked up',
        'Actions'
    ];

    const columns = {
        functionParams : [
            'orderId'
        ],
        dataKeys       : [
            'orderId',
            'orderNumber',
            'status',
            'paymentStatus',
            'customer',
            'orderDate',
            'paymentMethod',
            'invoiceNumber',
            'productName',
            'origin',
            'bruttoPrice',
            'pickedUp'
        ],
    }

    const handleShowIconClick = (orderId) => {
        navigate('/orders/' + orderId + '/items');
    }

    return <Fragment>
        <Box>
            <div className="flex flex-wrap items-center justify-between gap-3 my-4">
                <div className="flex flex-wrap gap-3">
                    <select
                        id="customerId"
                        name="customerId"
                        onChange={(event) => {
                            const value = event.target.value === "" ? null : event.target.value;
                            setFilterCustomerId(value);
                        }}
                        className="border p-2"
                    >
                        <option value="">Filter by customers</option>
                        {customers && customers.map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.firstName} {option.lastName} {option.email ? `(${option.email})` : ''}
                            </option>
                        ))}
                    </select>
                    <select
                        id="paymentStatus"
                        name="paymentStatus"
                        onChange={(event) => {
                            const value = event.target.value === "" ? null : event.target.value;
                            setFilterPaymentStatus(value);
                            setPage(1);
                        }}
                        className="border p-2"
                    >
                        <option value="">Filter by payment status</option>
                        {getPaymentStatuses().map((option) => (
                            <option key={option.id} value={option.id}>
                                {option.name}
                            </option>
                        ))}
                    </select>
                    <input
                        type="date"
                        className="border p-2"
                        onChange={(event) => setFilterFromDate(event.target.value)}
                    />
                    <input
                        type="date"
                        className="border p-2"
                        onChange={(event) => setFilterToDate(event.target.value)}
                    />
                </div>
                <Button title='Download report' handleClick={handleDownloadReportButtonClick}/>
            </div>
            <h2 className="text-md font-bold text-gray-800 mb-4 text-center">
                Total incl. VAT: €{total}
            </h2>
            <ListEdit
                headers={listHeaders}
                items={orderList}
                columns={columns}
                pagination={pagination}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
                handleInvoiceIconClick={handleInvoiceIconClick}
                handleCancelIconClick={handleCancelIconClick}
                handleShowIconClick={handleShowIconClick}
            />
        </Box>
        {showCancelModal && (
            <Modal>
                <form onSubmit={handleCancelSubmit}>
                    <ModalForm
                        title={'Are you sure you want to cancel this order?'}
                        saveButtonTitle={'OK'}
                        handleCancelButtonClick={() => setShowCancelModal(false)}
                    >
                        <div className="flex flex-col">
                            <p className="text-sm text-gray-600">
                                This action will:
                            </p>
                            <ul className="list-disc list-inside text-sm text-gray-600">
                                <li>A negative payment will be created if a payment is linked to this order.</li>
                                <li>A cancellation invoice will be generated if an invoice is linked to this order.</li>
                                <li>The order status and payment status will be updated to "Cancelled."</li>
                                <li><b>Webshop:</b> if items are linked to this order, the reserved stock will be restored and the items will be removed from the customer's account.</li>
                                <li><b>Booking:</b> the ticket will become inactive, but any bookings made with it will NOT be cancelled automatically.</li>
                                <li><b>Important:</b> You must manually refund the payment through the PayPal, Stripe or Mollie dashboard!</li>
                            </ul>
                        </div>
                    </ModalForm>
                </form>
            </Modal>
        )}
    </Fragment>;
}

export default OrderList;